import * as React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getOptions, IFormStateValue } from '../utils/formUtils';
import { ILabel, ILabelOption } from '../constants/interfaces';
import FormField from './FormField';
import { formUi } from '../constants/globalcss';

import {
  buttonSizeMap,
  darkSecondaryButtonStyle,
} from '../constants/ButtonStyles';

export interface ITableProps {
  columns: ILabel[];
  resetTrigger: number;
  onRowsChange?: (rows: Array<Record<string, string>>) => void;
}

function isRowPartial(row: Record<string, string>, columns: ILabel[]) {
  const mandatoryCols = columns.filter((col) => col.isMandatory);
  const filled = mandatoryCols.filter(
    (col) =>
      row[col.labelId] !== '' &&
      row[col.labelId] !== null &&
      row[col.labelId] !== undefined
  );
  return filled.length > 0 && filled.length < mandatoryCols.length;
}

/**
 * Filters dropdown options to prevent "Down Payment" from being selected more than once
 * Only "Down Payment" is restricted, all other options can be selected multiple times
 * @param options - Original options array
 * @param formState - Form state (passed through for compatibility)
 * @param labelId - Current field's label ID
 * @param currentRowIdx - Current row index
 * @param tableRows - All table rows
 * @returns Filtered options array
 */
function getFilteredOptions(
  options: ILabelOption[],
  formState: Record<string, IFormStateValue>,
  labelId: number,
  currentRowIdx: number,
  tableRows: Record<string, string>[]
): ILabelOption[] {
  // First apply the existing filtering logic
  const baseOptions = getOptions(options, formState);

  // Check if any other row has already selected "Down Payment" for this field
  const downPaymentAlreadySelected = tableRows.some((row, rowIdx) => {
    return (
      rowIdx !== currentRowIdx &&
      row[labelId] &&
      String(row[labelId]).toLowerCase().trim() === 'down payment'
    );
  });

  // If "Down Payment" is already selected in another row, filter it out
  // Only filter "Down Payment" - allow all other options to be selected multiple times
  if (downPaymentAlreadySelected) {
    return baseOptions.filter(
      (option) => option.text.toLowerCase().trim() !== 'down payment'
    );
  }

  return baseOptions;
}

interface MuiTableRowProps {
  row: Record<string, string>;
  rowIdx: number;
  columns: ILabel[];
  onCellChange: (rowIdx: number, key: string, value: string) => void;
  onDelete: (rowIdx: number) => void;
  rowErrors: Record<string, string>;
  isPartial: boolean;
  tableRows: Record<string, string>[];
}

const MuiTableRow = React.memo(
  ({
    row,
    rowIdx,
    columns,
    onCellChange,
    onDelete,
    rowErrors,
    isPartial,
    tableRows,
  }: MuiTableRowProps) => {
    return (
      <Paper
        elevation={1}
        sx={{
          backgroundColor: isPartial ? '#fff4f4' : formUi.card.background,
          border: isPartial ? '1px solid #e57373' : formUi.card.border,
          borderRadius: formUi.card.radius,
          boxShadow: formUi.card.shadow,
          mb: 2,
          p: formUi.spacing.cardPadding,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Typography fontWeight={600} variant="subtitle2">
            Row {rowIdx + 1}
          </Typography>
          <IconButton
            aria-label="delete row"
            onClick={() => onDelete(rowIdx)}
            size="small"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        <Grid container spacing={1.25}>
          {columns.map((col: ILabel) => (
            <Grid key={col.labelId} size={{ xs: 12, md: 6 }}>
              <FormField
                key={`${rowIdx}-${col.labelId}`}
                field={{
                  ...col,
                  options: getFilteredOptions(
                    col.options,
                    {},
                    col.labelId,
                    rowIdx,
                    tableRows
                  ),
                  type: col.type.replace('|column', ''),
                }}
                value={String(row[col.labelId] || '')}
                errorText={rowErrors?.[rowIdx]?.[col.labelId] || ''}
                handleInputChange={(_, value) =>
                  onCellChange(rowIdx, String(col.labelId), String(value))
                }
                width="100%"
              />
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }
);

export default function MuiTable({
  columns,
  resetTrigger,
  onRowsChange,
}: ITableProps) {
  const [tableRows, setTableRows] = React.useState([
    {} as Record<string, string>,
  ]);
  const [rowErrors, setRowErrors] = React.useState<{
    [rowIdx: number]: { [colId: string]: string };
  }>({});

  React.useEffect(() => {
    if (resetTrigger) {
      setTableRows([{} as Record<string, string>]);
      setRowErrors({});
    }
  }, [resetTrigger]);

  React.useEffect(() => {
    if (onRowsChange) {
      const validRows = tableRows.filter((row, idx) =>
        columns
          .filter((col) => col.isMandatory)
          .every(
            (col) =>
              row[col.labelId] !== '' &&
              row[col.labelId] !== null &&
              row[col.labelId] !== undefined
          )
      );
      onRowsChange(validRows);
    }
  }, [tableRows, columns, onRowsChange]);

  const handleAddRow = React.useCallback(() => {
    const emptyRow: Record<string, string> = {};
    columns.forEach((col) => {
      emptyRow[col.labelId] = '';
    });
    setTableRows((prevRows) => [...prevRows, emptyRow]);
  }, [columns]);

  const handleDeleteRow = React.useCallback((rowIdx: number) => {
    setTableRows((prevRows) => prevRows.filter((_, idx) => idx !== rowIdx));
  }, []);

  const handleCellChange = React.useCallback(
    (rowIdx: number, key: string, value: string) => {
      // Check if trying to select "Down Payment" when it's already selected in another row
      // Only restrict "Down Payment" - allow all other options to be selected multiple times
      if (value && value.toLowerCase().trim() === 'down payment') {
        const downPaymentAlreadySelected = tableRows.some((row, idx) => {
          return (
            idx !== rowIdx &&
            row[key] &&
            String(row[key]).toLowerCase().trim() === 'down payment'
          );
        });

        if (downPaymentAlreadySelected) {
          // Don't allow the change and show an error
          setRowErrors((prevErrors) => ({
            ...prevErrors,
            [rowIdx]: {
              ...prevErrors[rowIdx],
              [key]: 'Down Payment can only be selected once',
            },
          }));
          return;
        }
      }

      setTableRows((prevRows) =>
        prevRows.map((row, idx) =>
          idx === rowIdx ? { ...row, [key]: value } : row
        )
      );
      setRowErrors((prevErrors) => {
        const updatedRow = { ...tableRows[rowIdx], [key]: value };
        return {
          ...prevErrors,
          [rowIdx]: validateRow(updatedRow, columns, tableRows, rowIdx),
        };
      });
    },
    [columns, tableRows]
  );

  return (
    <Paper
      sx={{
        backgroundColor: formUi.card.background,
        border: formUi.card.border,
        borderRadius: formUi.card.radius,
        boxShadow: formUi.card.shadow,
        my: 1.5,
        p: formUi.spacing.cardPadding,
      }}
    >
      {tableRows.length === 0 ? (
        <Typography align="center" sx={{ py: 3 }}>
          No data. Click 'Add Row' to start.
        </Typography>
      ) : (
        tableRows.map((row, rowIdx) => (
          <MuiTableRow
            key={rowIdx}
            row={row}
            rowIdx={rowIdx}
            columns={columns}
            onCellChange={handleCellChange}
            onDelete={handleDeleteRow}
            rowErrors={rowErrors[rowIdx] || {}}
            isPartial={isRowPartial(row, columns)}
            tableRows={tableRows}
          />
        ))
      )}
      <Button
        variant="outlined"
        onClick={handleAddRow}
        startIcon={<AddIcon />}
        sx={{
          ...darkSecondaryButtonStyle,
          ...buttonSizeMap.medium,
          m: 2,
        }}
      >
        Add Row
      </Button>
    </Paper>
  );
}

function validateRow(
  row: Record<string, string>,
  columns: ILabel[],
  tableRows?: Record<string, string>[],
  currentRowIdx?: number
) {
  const errors: { [colId: string]: string } = {};

  columns.forEach((col) => {
    if (col.isMandatory && !row[col.labelId]) {
      errors[col.labelId] = 'Required';
    }

    // Check for Down Payment duplication if tableRows and currentRowIdx are provided
    // Only restrict "Down Payment" - allow all other options to be selected multiple times
    if (tableRows && currentRowIdx !== undefined && row[col.labelId]) {
      const value = row[col.labelId];
      if (String(value).toLowerCase().trim() === 'down payment') {
        const downPaymentAlreadySelected = tableRows.some((otherRow, idx) => {
          return (
            idx !== currentRowIdx &&
            otherRow[col.labelId] &&
            String(otherRow[col.labelId]).toLowerCase().trim() ===
              'down payment'
          );
        });

        if (downPaymentAlreadySelected) {
          errors[col.labelId] = 'Down Payment can only be selected once';
        }
      }
    }
  });

  return errors;
}
